// src/pages/ContactUs.jsx
import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCms } from '../../hooks/useCms';

export function ContactUs() {
  const { t } = useTranslation('contact');
  const { get } = useCms('contact');
  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    subject: '', 
    message: '' 
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);
    setSent(true);
  };

  // Contact info items
  const contactItems = [
    { 
      icon: Phone, 
      title: t('info.phone.title'), 
      lines: get('contact_information.phone', '').split('\n').filter(Boolean).length ? get('contact_information.phone').split('\n') : t('info.phone.lines', { returnObjects: true }) as string[],
      color: 'text-[#1E6DFF] bg-[#1E6DFF]/10' 
    },
    { 
      icon: Mail, 
      title: t('info.email.title'), 
      lines: get('contact_information.email', '').split('\n').filter(Boolean).length ? get('contact_information.email').split('\n') : t('info.email.lines', { returnObjects: true }) as string[],
      color: 'text-[#22C55E] bg-[#22C55E]/10' 
    },
    { 
      icon: MapPin, 
      title: t('info.address.title'), 
      lines: get('contact_information.address', '').split('\n').filter(Boolean).length ? get('contact_information.address').split('\n') : t('info.address.lines', { returnObjects: true }) as string[],
      color: 'text-[#F59E0B] bg-[#F59E0B]/10' 
    },
    { 
      icon: Clock, 
      title: t('info.hours.title'), 
      lines: get('contact_information.hours', '').split('\n').filter(Boolean).length ? get('contact_information.hours').split('\n') : t('info.hours.lines', { returnObjects: true }) as string[],
      color: 'text-[#717182] bg-[#E5E7EB]' 
    },
  ];

  // Subject options
  const subjectOptions = [
    { value: '', label: t('form.subject_placeholder') },
    { value: 'general', label: t('form.subject_options.general') },
    { value: 'listing', label: t('form.subject_options.listing') },
    { value: 'account', label: t('form.subject_options.account') },
    { value: 'payment', label: t('form.subject_options.payment') },
    { value: 'fraud', label: t('form.subject_options.fraud') },
    { value: 'partnership', label: t('form.subject_options.partnership') },
  ];

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#2D323A] to-[#1E2229] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl text-white mb-3">{get('header.title', t('hero.title'))}</h1>
          <p className="text-white/70">{get('header.description', t('hero.description'))}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Info */}
          <div className="space-y-5">
            {contactItems.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white rounded-2xl p-5 border border-[#E5E7EB] flex gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[#2D323A] mb-1">{item.title}</p>
                    {item.lines.map(l => (
                      <p key={l} className="text-sm text-[#717182]">{l}</p>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* WhatsApp */}
            <a 
              href="https://wa.me/212600000000" 
              target="_blank" 
              rel="noreferrer"
              className="bg-[#25D366] rounded-2xl p-5 text-white flex items-center gap-3 hover:bg-[#20bd5a] transition-colors"
            >
              <svg className="w-8 h-8 fill-white" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <div>
                <p>{t('info.whatsapp.title')}</p>
                <p className="text-sm text-white/80">{t('info.whatsapp.subtitle')}</p>
              </div>
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E5E7EB] p-6">
            {sent ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#22C55E]" />
                </div>
                <h2 className="text-2xl text-[#2D323A] mb-2">{t('form.success.title')}</h2>
                <p className="text-[#717182]">{t('form.success.description')}</p>
              </div>
            ) : (
              <>
                <h2 className="text-xl text-[#2D323A] mb-5">{t('form.title')}</h2>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-[#2D323A] mb-1.5">
                        {t('form.name')}
                      </label>
                      <input 
                        value={form.name} 
                        onChange={e => setForm({ ...form, name: e.target.value })} 
                        placeholder={t('form.name_placeholder')} 
                        className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#2D323A] mb-1.5">
                        {t('form.email')}
                      </label>
                      <input 
                        type="email" 
                        value={form.email} 
                        onChange={e => setForm({ ...form, email: e.target.value })} 
                        placeholder={t('form.email_placeholder')} 
                        className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-[#2D323A] mb-1.5">
                      {t('form.subject')}
                    </label>
                    <select 
                      value={form.subject} 
                      onChange={e => setForm({ ...form, subject: e.target.value })} 
                      className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]"
                    >
                      {subjectOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#2D323A] mb-1.5">
                      {t('form.message')}
                    </label>
                    <textarea 
                      value={form.message} 
                      onChange={e => setForm({ ...form, message: e.target.value })} 
                      rows={6} 
                      placeholder={t('form.message_placeholder')} 
                      className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] resize-none" 
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={loading} 
                    className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                    {loading ? t('form.button_loading') : t('form.button')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
